<template>
  <div>
    <div id="overlay" v-if="fetchingData">
      <div class="spinner"></div>
    </div>

    <h1>Open Burn Application</h1>

    <!--
    <p v-if="form.projectType === 'demolition'">
      To complete the {{form.projectType}} permit application you will need the following documents:
    </p>

    <ol v-if="form.projectType === 'demolition'">
      <li>Plat of Survey/Arial View of Site</li>
      <li>Cook County Property Tax Summary</li>
      <li>Asbestos Inspection Report</li>
    </ol>
    -->

    <div class="form-wrapper">
      <div class="form-navbar">
        <div class="title-nav">
          <label class="col-form-label ml-2 d-none d-sm-block">
            Open Burn Application
          </label>
          <div class="btn-group" role="group" aria-label="Save And Close Buttons">
            <button class="btn btn-link"
                    @click="saveApplication"
                    v-bind:disabled="!canSave">
              <font-awesome-icon :icon="['far', 'save']" />
              Save <span class="d-none d-lg-inline-block">&amp; Return Later</span>
            </button>
            <button class="btn btn-link" @click="cancel">
              <font-awesome-icon icon="times" />
              Cancel
            </button>
            <button class="btn btn-link"
                    v-if="isLastPage"
                    @click="submitForm">
              <font-awesome-icon :icon="['far', 'check-circle']" />
              Submit Form
            </button>
          </div>
        </div>

        <div class="nav-links">
          <a class="btn btn-link end" @click="previousSection">
            <font-awesome-icon icon="chevron-left" />
          </a>
          <router-link v-for="(route, index) in routes"
                       :to="{name: route, query: $route.query}"
                       v-bind:key="route"
                       v-bind:class="{visited: index < currentRoute, error: $v.pages[route].$error }"
                       v-bind:style="[form.files.statePermit == null && form.existingFileDetails.statePermit == null ? { 'pointer-events': 'none' } : {}]">
            {{index + 1}}
          </router-link>
          <a class="btn btn-link end" @click="nextSection"  v-bind:style="[form.files.statePermit == null && form.existingFileDetails.statePermit == null ? { 'pointer-events': 'none' } : {}]">
            <font-awesome-icon icon="chevron-right" />
          </a>
        </div>
      </div>
      <div class="section-title">
        {{routeNames[currentRoute]}}
      </div>

      <div class="row no-gutters">
        <div class="col" style="padding: 1%;">
          <router-view :form="form"
                       :val="$v"
                       class="h-100 d-flex align-content-between flex-wrap"
                       v-bind:fetchingData.sync="fetchingData"
                       v-bind:title.sync="title">
          </router-view>
        </div>
        <div class="col-3 d-md-block d-none">
          <div class="save-panel">
            <div class="button-wrapper">
              <button class="btn btn-primary"
                      @click="saveApplication"
                      v-bind:disabled="!canSave">
                <font-awesome-icon :icon="['far', 'save']" />
                Save <span class="d-none d-lg-inline-block">&amp; Return Later</span>
              </button>
              <div class="w-100"></div>
              <button class="btn btn-primary mt-2" @click="cancel">
                <font-awesome-icon icon="times" />
                Cancel
              </button>
              <div class="w-100"></div>
              <button class="btn btn-success mt-2"
                      v-if="isLastPage"
                      @click="submitForm">
                <font-awesome-icon :icon="['far', 'check-circle']" />
                Submit Form
              </button>

              <dl class="row justify-content-end mt-2">
                <dt class="col" v-if="filingFee > 0">Filing Fee:</dt>
                <dd class="col-auto" v-if="filingFee > 0">${{filingFee}}</dd>
                <div class="w-100"></div>
                <dt class="col" v-if="burnSessionFee > 0">Burn Session Fee:</dt>
                <dd class="col-auto" v-if="burnSessionFee > 0">${{burnSessionFee}}</dd>
                <div class="w-100"></div>
                <dt class="col">
                  <strong>Total Price:</strong>
                </dt>
                <dd class="col-auto">
                  <strong>${{totalFee}}</strong>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { OpenBurnPermitApplication, OpenBurnFormData } from '~/js/FormObjects';
import { OpenBurnPermitApplicationValidation } from '@/js/FormValidations';
import { getApplication, getPrices, capitalize, errorNotification, getOpenBurnFeeStatus } from '~/js/FormFunctions';
import { CapitalizeFilter } from '@/js/Mixins';


const sumReducer = (accumulator, currentValue) => accumulator + currentValue;

export default {
  name: 'Application',
  props: {
  },
  mixins: [CapitalizeFilter],
  data() {
    return {
      form: new OpenBurnPermitApplication(),
      formFieldData: new OpenBurnFormData(),
      title: null,
      currentRoute: 0,
      routes: [
        'PermitVerification',
        'ApplicantInformation',
        'BurnInformation',
        'CertificationInformation',
      ],
      routeNames: [
        'Permit Verification',
        'Applicant Information',
        'Burn Information',
        'Certification Information',
      ],
      permitPrices: [],
      fetchingData: false,
    };
  },
  mounted() {
    const self = this;
    if (this.$route.params.form != undefined) {
      // this.form = this.$route.params.form
      this.form = this.$route.params.form;
    } else {
      this.form.permitNumber = '';
    }
    this.$router.push({ name: 'PermitVerification', query: this.$route.query });
  },
  validations: {
    form: new OpenBurnPermitApplicationValidation(),
    pages: {
      PermitVerification: ['form.isMunicipality', 'form.files.statePermit'],
      ApplicantInformation: ['form.applicantFirstName', 'form.applicantLastName', 'form.applicantPhone', 'form.applicantAddress', 'form.applicantEmail', 'form.confirmEmail', 'form.typeOfRequest', 'form.typeOfRequestOtherDesc', 'form.files.asbestosAssessmentFile'],
      BurnInformation: ['form.siteName', 'form.otherDate', 'form.contactFirstName', 'form.contactLastName', 'form.contactPhone', 'form.burnMaterials', 'form.methodOfExtinguishment', 'form.totalSessions', 'form.siteCity', 'form.crossStreets'],
      // AdditionalInformation: ['form.particulateMatterBS', 'form.sulfurDioxideBS', 'form.individualsNotifiedExplaination', 'form.nitrogenOxideBS', 'form.organicMaterialBS', 'form.carbonMonoxideBS', 'form.otherDsc', 'form.otherBS',
      // 'form.disposalMethod', 'form.abatementOtherDesc', 'form.abatementMethodsDesc', 'form.nameOfDevice', 'form.manufacturer', 'form.modelNo', 'form.files.writtenInstructions',
      // 'form.typeOfDisaster', 'form.disasterDeclaredBy', 'form.ffTrainingOtherDesc', 'form.ffDescRelatedToExersise'],
      CertificationInformation: ['form.signature', 'form.signatureDate', 'form.signatureTitle'],
    },
  },
  methods: {
    previousSection() {
      if (this.currentRoute > 0) {
        this.$router.push({ name: this.routes[this.currentRoute - 1], query: this.$route.query });
      }
    },
    nextSection() {
      if (this.currentRoute < this.routes.length) {
        this.$router.push({ name: this.routes[this.currentRoute + 1], query: this.$route.query });
      }
    },
    submitForm() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        this.$notify('Errors during permit application validation.', 'error');
        return;
      }

      const vm = this;
      this.mySaveApplication('submit')

        .catch(() => { });
    },
    saveApplication() {
      const self = this;
      this.mySaveApplication()
        .then((response) => {
          self.$router.push({
            name: 'PermitApplicationSaved',
            params: {
              projectType: self.projectType.toLowerCase(),
            },
            query: {
              applicationId: response.data,
            },
          });
        })
        .catch(() => { });
    },
    mySaveApplication(action) {
      const self = this;

      const formData = new FormData();
      Object.keys(this.form.files).forEach((key) => {
        if (self.form.files[key]) {
          formData.append(key, self.form.files[key]);
        }
      });

      formData.append('form', JSON.stringify(this.form));
      this.fetchingData = true;

      return new Promise((resolve, reject) => {
        self.$http.post('/api/openburn/save', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
          .then((response) => {
            if (action == 'submit') {
              Object.keys(self.form.files).forEach((key) => {
                if (self.form.files[key]) {
                  self.form.existingFileDetails[key] = {
                    documentName: key,
                    name: self.form.files[key].name,
                  };
                }
              });
              self.form.permitNumber = response.data;
              getOpenBurnFeeStatus(self.form.permitNumber)
                .then((result) => {
                  self.$store.commit('application', self.form);
                  self.$router.push({ name: 'OpenBurnReview', params: { hasFee: result } });
                });
            } else {
              self.$router.push({
                name: 'OpenBurnPermitApplicationSaved',
                query: {
                  applicationId: response.data,
                },
              });
            }
          })

          .catch((error) => {
            self.fetchingData = false;
            errorNotification('Error saving application record: ', error);
            reject(error);
          });
      });
    },
    cancel() {
      this.$router.push({ name: 'PermitApplicationCancel', params: { projectType: this.projectType } });
    },
  },
  watch: {
    $route(to) {
      this.currentRoute = this.routes.findIndex(route => to.name.endsWith(route));
    },
  },
  computed: {
    isLastPage() {
      return this.currentRoute === this.routes.length - 1;
    },
    canSave() {
      return true;
      // return !this.$v.form.confirmEmail.$invalid
    },
    filingFee() {
      if (this.form.isMunicipality == true || this.form.isMunicipality == 'true') {
        return 0;
      }
      return 20;
    },
    burnSessionFee() {
      if (this.form.isMunicipality == true || this.form.isMunicipality == 'true') {
        return 0;
      }
      if (this.form.totalSessions == null) {
        return 0;
      }
      return Number(this.form.totalSessions) * 36;
    },
    totalFee() {
      return this.filingFee +
          this.burnSessionFee;
    },
  },
};
</script>
